import { ReactNode, useState } from 'react'

import { Portal } from 'ui/components/Portal'
import { useDelayMount } from 'ui/hooks/useDelayMount'

import { useLeaveConfirmation } from '../LeaveConfirmation'
import { Box } from './Box'
import { DialogContext } from './context'

import * as SC from './styles'

type Props = {
  isOpened?: boolean
  isStory?: boolean
  id: string
  content: ReactNode

  onClose?: () => void
}

/**
 * How to use
 *
 * @example
 * ```
 * ../index.jsx
 *
 * const [showDialog, setShowDialog] = useState(false);
 *
 * <Dialog
 *   id="id"
 *   isOpened={showDialog}
 *   content={<Content />}
 *   onClose={() => setShowDialog(false)}
 * />
 *
 * <Button onClick={() => setShowDialog(true)}> Edit </Button>
 *
 * ../Content.jsx
 *
 * const Content = () => {
 *   dialog = useDialog();
 *
 *   // create form
 *
 *   const submitHandler = () => {
 *     // actions
 *     // if we want to close dialog on action complete, it ignores leave confirm settings:
 *     dialog.anywayClose()
 *   };
 *
 *   // for leave confirmation, typically it true when form are dirty
 *   useEffect(() => {
 *     dialog.doNotClose(preventLeaving)
 *   }, [preventLeaving]);
 *
 *   return (
 *     <>
 *       <DialogHeader title={'title'} subTitle={'subTitle'} action={<Button>Action</Button>} />
 *       <DialogContent withoutPadding={true or false}>
 *         content
 *       </DialogContent>
 *       <DialogFooter hasSubmit hasCancel onSubmit={form.handleSubmit} />
 *     </>
 *   )
 * }
 * ```
 */
export const Dialog = (props: Props) => {
  const { isOpened = false, isStory = false, id, content, onClose } = props

  const [isDoNotClose, setIsDoNotClose] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const isShowDialog = useDelayMount(isOpened, 250)
  const { showLeaveConfirmation } = useLeaveConfirmation(isDoNotClose)

  const closeAction = () => {
    if (isPending) {
      return
    }
    if (isDoNotClose && !showLeaveConfirmation()) {
      return
    }
    if (onClose) {
      onClose()
    }
  }

  const anywayCloseAction = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    isShowDialog && (
      <DialogContext.Provider
        value={{
          isOpened,
          isPending,
          isDoNotClose,
          doNotClose: setIsDoNotClose,
          pending: setIsPending,
          close: closeAction,
          anywayClose: anywayCloseAction
        }}
      >
        {isStory ? (
          <Box isStory>{content}</Box>
        ) : (
          <Portal id={id}>
            <SC.Overlay $opened={isOpened}>
              <Box>{content}</Box>
            </SC.Overlay>
          </Portal>
        )}
      </DialogContext.Provider>
    )
  )
}
